import { LESSON_STAGE_KEYS } from './progressionConstants';

export const QUICK_LESSON_OUTCOME_OPTIONS = [
  'not_ready_yet',
  'in_progress',
  'strong_progress',
  'achieved',
];

function getStageIndex(stageKey) {
  return LESSON_STAGE_KEYS.indexOf(stageKey);
}

export function inferFurthestStageReached(stages = []) {
  const sorted = (stages || [])
    .map((stage) => ({
      stage: stage.stage,
      completionStatus: stage.completionStatus || 'not_attempted',
    }))
    .sort((a, b) => getStageIndex(a.stage) - getStageIndex(b.stage));

  const furthestAttempted = [...sorted]
    .reverse()
    .find((stage) => stage.completionStatus === 'attempted' || stage.completionStatus === 'completed');

  return furthestAttempted?.stage || LESSON_STAGE_KEYS[0];
}

export function inferQuickOutcomeChoice(lessonInstance = {}) {
  const status = lessonInstance?.lessonOutcome?.status || 'in_progress';
  const confidence = lessonInstance?.lessonOutcome?.skillConfidence || 'developing';

  if (confidence === 'ready_to_progress') return 'achieved';
  if (confidence === 'solid' || status === 'completed') return 'strong_progress';
  if (status === 'needs_review' || confidence === 'low') return 'not_ready_yet';
  return 'in_progress';
}

function mapOutcomeChoiceToLessonOutcome(outcomeChoice, lessonInstance = {}) {
  const existing = lessonInstance?.lessonOutcome || {};

  switch (outcomeChoice) {
    case 'achieved':
      return {
        ...existing,
        status: 'completed',
        skillConfidence: 'ready_to_progress',
      };
    case 'strong_progress':
      return {
        ...existing,
        status: 'completed',
        skillConfidence: 'solid',
      };
    case 'not_ready_yet':
      return {
        ...existing,
        status: 'needs_review',
        skillConfidence: 'low',
      };
    case 'in_progress':
    default:
      return {
        ...existing,
        status: 'in_progress',
        skillConfidence: 'developing',
      };
  }
}

// Pass 84.1 — the CLOSEOUT-safe transform. Unlike applyQuickLessonLogToLessonInstance (which REBUILDS every canonical
// stage's completionStatus from a supplied `furthestStageReached`, and so would downgrade later stages to
// 'not_attempted'), this transform is ADDITIVE and PRESERVES all pre-existing structured progress. It NEVER touches
// `stages` (so completionStatus / plannedDrillIds / plannedDrills / stage instructorNotes survive byte-for-byte). It only
// writes the human closeout note into `lessonOutcome.instructorSummary`, and applies an outcome status/skillConfidence
// ONLY when the instructor EXPLICITLY chose one (`outcomeChoice` truthy). When no explicit outcome is given, existing
// `lessonOutcome.status` / `skillConfidence` / `recommendedNextSkillId` are preserved — no hidden/inferred mutation.
export function applyCloseoutToLessonInstance(
  lessonInstance = {},
  { quickNote = '', outcomeChoice = null, recommendedNextSkillId = '' } = {}
) {
  const existingOutcome = lessonInstance.lessonOutcome || {};
  const outcomeEdit = outcomeChoice ? mapOutcomeChoiceToLessonOutcome(outcomeChoice, lessonInstance) : null;
  return {
    ...lessonInstance,
    // stages intentionally NOT rebuilt — pre-existing structured progress is preserved.
    lessonOutcome: {
      ...existingOutcome,
      ...(outcomeEdit ? { status: outcomeEdit.status, skillConfidence: outcomeEdit.skillConfidence } : {}),
      recommendedNextSkillId: recommendedNextSkillId || existingOutcome.recommendedNextSkillId || '',
      instructorSummary: quickNote || existingOutcome.instructorSummary || '',
    },
  };
}

export function applyQuickLessonLogToLessonInstance(
  lessonInstance = {},
  {
    furthestStageReached = LESSON_STAGE_KEYS[0],
    outcomeChoice = 'in_progress',
    quickNote = '',
    recommendedNextSkillId = '',
  } = {}
) {
  const furthestStageIndex = Math.max(0, getStageIndex(furthestStageReached));
  const strengthenSelectedStage = outcomeChoice === 'strong_progress' || outcomeChoice === 'achieved';

  const nextStages = LESSON_STAGE_KEYS.map((stageKey, index) => {
    const existingStage = (lessonInstance.stages || []).find((stage) => stage.stage === stageKey) || {};
    let completionStatus = 'not_attempted';

    if (index < furthestStageIndex) {
      completionStatus = 'completed';
    } else if (index === furthestStageIndex) {
      completionStatus = strengthenSelectedStage ? 'completed' : 'attempted';
    }

    return {
      ...existingStage,
      stage: stageKey,
      title: existingStage.title || stageKey.replace(/_/g, ' '),
      plannedDrillIds: existingStage.plannedDrillIds || [],
      plannedDrills: existingStage.plannedDrills || [],
      instructorNotes:
        index === furthestStageIndex && quickNote
          ? quickNote
          : existingStage.instructorNotes || '',
      completionStatus,
    };
  });

  return {
    ...lessonInstance,
    stages: nextStages,
    lessonOutcome: {
      ...mapOutcomeChoiceToLessonOutcome(outcomeChoice, lessonInstance),
      recommendedNextSkillId:
        recommendedNextSkillId || lessonInstance?.lessonOutcome?.recommendedNextSkillId || '',
      instructorSummary: quickNote || lessonInstance?.lessonOutcome?.instructorSummary || '',
    },
  };
}
